//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import * as galleriesApi from '@apis/galleries/galleriesApi';
import {
  IGallery,
  ISelfServeGallery,
} from '@apis/galleries/galleriesApiModels';
import { actions as galleriesCustomizationActions } from '@galleries/actions/galleriesCustomizationActions';
import { deleteGalleryActions } from '@galleries/actions/galleriesDeleteGalleryActions';
import { actions as mediaAutoFillActions } from '@galleries/actions/galleriesMediaAutofillModalActions';
import { galleryPickerActions } from '@galleries/actions/galleriesPickerActions';
import {
  FAKE_BAZAARVOICE_NETWORK_CONTAINER_ID,
  FAKE_BAZAARVOICE_NETWORK_REEL_ID,
  GALLERIES_ACTIONS,
  getAutofillText,
} from '@galleries/constants/galleriesConstants';
import { ReelAutoApprovalSourceType } from '@galleries/models/AutoApprovalConfiguration';
import GalleryDisplayName from '@galleries/models/GalleryDisplayName';
import {
  GALLERY_LOCATION,
  GALLERY_TYPE,
  InstaShopAutoApprovalSources,
} from '@galleries/models/galleriesStateModel';
import { galleriesDataActions } from '@galleries/reducers/galleriesDataSlice';
import { getIsBazaarvoiceNetworkGallerySelected } from '@galleries/selectors/getIsBazaarvoiceNetworkGallerySelected';
import getTypeOfGalleryLabel from '@galleries/selectors/getTypeOfGalleryLabel';
import { galleryLocationToContainerType } from '@galleries/utils/galleryTypeUtils';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ICuralateStore } from '@models/AppState';
import { ExperienceContainerType } from '@models/ExperienceContainerType';
import { SocialNetwork } from '@models/SocialNetwork';
import { navigateToState } from '@react-bridge/wrappers/navigation';
import { createAction } from '@utils/actions';
import { isDefined } from '@utils/filterPredicates';

import { toastNotificationsActions } from 'app/components/common/toast-notifications/toastNotificationsActions';
import { ToastType } from 'app/components/common/toast-notifications/toastNotificationsModel';
import { crl8 } from 'app/curalate.globals';
import { ReduxDispatch, ReduxGetState } from 'app/curalate.store';
import gaApi from 'app/initialization/ga/ga-api';

// The number of items to approve initially so that users creating InstaShop galleries
// have content to use while the backfill is running.
const INITIAL_ITEMS_APPROVAL_COUNT = 15;

function openCreateGalleryModal() {
  return createAction({
    type: GALLERIES_ACTIONS.OPEN_CREATE_GALLERY_MODAL,
  });
}

function closeCreateGalleryModal() {
  return createAction({
    type: GALLERIES_ACTIONS.CLOSE_CREATE_GALLERY_MODAL,
  });
}

function setTypeOfGallery(typeOfGallery: GALLERY_TYPE) {
  return createAction({
    type: GALLERIES_ACTIONS.SET_TYPE_OF_GALLERY,
    typeOfGallery,
  });
}

function setGalleryLocation(galleryLocation: GALLERY_LOCATION) {
  return createAction({
    type: GALLERIES_ACTIONS.SET_GALLERY_LOCATION,
    galleryLocation,
  });
}

function setAutoApprovalStrategy(
  autoApprovalStrategy: InstaShopAutoApprovalSources,
) {
  return createAction({
    type: GALLERIES_ACTIONS.SET_AUTO_APPROVAL_STRATEGY,
    autoApprovalStrategy,
  });
}

function setAutoApprovalEnabled(isAutoApprovalEnabled: boolean) {
  return createAction({
    type: GALLERIES_ACTIONS.SET_AUTO_APPROVAL_ENABLED,
    isAutoApprovalEnabled,
  });
}

function setNewGalleryNameAction(galleryName: string) {
  return createAction({
    type: GALLERIES_ACTIONS.SET_NEW_GALLERY_NAME,
    galleryName,
  });
}

function setNewGalleryNameError(galleryNameError: string) {
  return createAction({
    type: GALLERIES_ACTIONS.SET_NEW_GALLERY_NAME_ERROR,
    galleryNameError,
  });
}

function clearNewGalleryNameError() {
  return createAction({
    type: GALLERIES_ACTIONS.SET_NEW_GALLERY_NAME_ERROR,
    galleryNameError: undefined,
  });
}

function requestCreateGallery() {
  return createAction({
    type: GALLERIES_ACTIONS.REQUEST_CREATE_GALLERY,
  });
}

function receiveCreateGallerySuccess(gallery: IGallery) {
  return createAction({
    type: GALLERIES_ACTIONS.RECEIVE_CREATE_GALLERY_SUCCEEDED,
    gallery,
  });
}

function receiveCreateGalleryFailed(error: string) {
  return createAction({
    type: GALLERIES_ACTIONS.RECEIVE_CREATE_GALLERY_FAILED,
    error,
  });
}

function clearCreateGalleryError() {
  return createAction({
    type: GALLERIES_ACTIONS.CLEAR_CREATE_GALLERY_ERROR,
    createGalleryError: undefined,
  });
}

export type GalleriesCreationActionType = ReturnType<
  | typeof openCreateGalleryModal
  | typeof closeCreateGalleryModal
  | typeof setTypeOfGallery
  | typeof setGalleryLocation
  | typeof setAutoApprovalStrategy
  | typeof setAutoApprovalEnabled
  | typeof setNewGalleryNameAction
  | typeof setNewGalleryNameError
  | typeof clearNewGalleryNameError
  | typeof requestCreateGallery
  | typeof receiveCreateGallerySuccess
  | typeof receiveCreateGalleryFailed
  | typeof clearCreateGalleryError
>;

export const galleriesCreationActions = {
  closeCreateGalleryModal,
  setTypeOfGallery,
  setGalleryLocation,
  setAutoApprovalStrategy,
  setAutoApprovalEnabled,
  setNewGalleryNameError,

  openCreateGalleryModal: () => (dispatch: ReduxDispatch) => {
    gaApi.recordEvent(
      gaApi.CATEGORY_GALLERIES,
      gaApi.ACTION_GALLERIES_CREATE_GALLERY_CTA_CLICKED,
    );
    return dispatch(openCreateGalleryModal());
  },

  setNewGalleryName:
    (galleryName: string) =>
    (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      if (getState().galleries.creation.errors.galleryNameError) {
        dispatch(clearNewGalleryNameError());
      }

      dispatch(setNewGalleryNameAction(galleryName));
    },

  createAutoApprovalConfig:
    (
      autoApprovalStrategy: InstaShopAutoApprovalSources,
      isInstashop: boolean,
      gallery?: IGallery,
      isInitialGalleryCreation?: boolean,
    ) =>
    async (dispatch: ReduxDispatch, getState: ReduxGetState) => {
      const { connectedInstagramAccount } =
        getState().facebookInstagramSettings;
      const isBazaarvoiceNetworkGallerySelected =
        getIsBazaarvoiceNetworkGallerySelected(getState());

      if (!connectedInstagramAccount) {
        dispatch(
          galleriesCreationActions.displayDisconnectedFacebookAccountToast(),
        );
        return;
      }

      const reelId = gallery?.reelId ?? FAKE_BAZAARVOICE_NETWORK_REEL_ID;

      const autoApprovalStrategyToUse = isBazaarvoiceNetworkGallerySelected
        ? InstaShopAutoApprovalSources.allMedia
        : autoApprovalStrategy;

      await galleriesApi.createAutoApprovalConfig({
        sourceId: connectedInstagramAccount.businessAccountId,
        sourceName: connectedInstagramAccount.username,
        sourceNetwork: SocialNetwork.Instagram,
        sourceType: ReelAutoApprovalSourceType.instagramBusinessUser,
        destinationReelId: reelId,
        autoApprovalStrategy: autoApprovalStrategyToUse,
      });

      const containerId =
        gallery?.containerId ?? FAKE_BAZAARVOICE_NETWORK_CONTAINER_ID;

      // In addition to selecting a gallery, this action will also fetch that gallery's auto approval configs
      await dispatch(galleryPickerActions.selectGallery(containerId));

      const { id: autoApprovalConfigId } =
        getState().galleries.data.selectedReelAutoApprovalConfig ?? {};

      if (isInitialGalleryCreation && isDefined(autoApprovalConfigId)) {
        await galleriesApi.approveNItems(
          autoApprovalConfigId,
          INITIAL_ITEMS_APPROVAL_COUNT,
        );
        galleriesApi.backfillAutoApprovalConfig(autoApprovalConfigId);

        dispatch(
          mediaAutoFillActions.setReadyToShowMediaAutofillModal(
            true,
            getAutofillText(isInstashop),
          ),
        );
      }
    },

  createNewGalleryOrInstashop:
    () => async (dispatch: ReduxDispatch, getState: ReduxGetState) => {
      const {
        autoApprovalStrategy,
        errors,
        galleryLocation,
        galleryName,
        isAutoApprovalEnabled,
        isCreatingGallery,
        typeOfGallery,
      } = getState().galleries.creation;

      const { connectedInstagramAccount } =
        getState().facebookInstagramSettings;

      if (isCreatingGallery) {
        return;
      }

      dispatch(requestCreateGallery());

      if (errors.createGalleryError) {
        dispatch(clearCreateGalleryError());
      }

      const isInstashop = typeOfGallery === GALLERY_TYPE.INSTASHOP;

      // There are two cases where we need to create an auto approval config:
      // 1. This is an instashop
      // 2. This is a standard gallery, but the client has chosen to auto approve brand content
      const isUsingAutoApproval =
        isInstashop || (!isInstashop && isAutoApprovalEnabled);

      if (isUsingAutoApproval && !connectedInstagramAccount) {
        dispatch(
          receiveCreateGalleryFailed('An error occurred. Please try again'),
        );
        dispatch(
          galleriesCreationActions.displayDisconnectedFacebookAccountToast(),
        );
        dispatch(closeCreateGalleryModal());
        return;
      }

      const displayName = GalleryDisplayName(galleryName);

      const containerType = isInstashop
        ? ExperienceContainerType.INSTASHOP
        : galleryLocationToContainerType(galleryLocation);

      const instashopLocation = isInstashop
        ? galleryLocationToContainerType(galleryLocation)
        : undefined;

      let gallery: ISelfServeGallery | undefined;
      try {
        gallery = await galleriesApi.createGallery(
          containerType,
          displayName,
          instashopLocation,
        );

        // Refetch gallery data after the create has completed
        await dispatch(galleriesDataActions.getGalleries());
        // select gallery after creation
        await dispatch(galleryPickerActions.selectGallery(gallery.containerId));

        if (isUsingAutoApproval) {
          await dispatch(
            galleriesCreationActions.createAutoApprovalConfig(
              autoApprovalStrategy,
              isInstashop,
              gallery,
              true, // isInitialGalleryCreation
            ),
          );
        }
        dispatch(receiveCreateGallerySuccess(gallery));

        gaApi.recordEvent(
          gaApi.CATEGORY_GALLERIES,
          gaApi.ACTION_GALLERIES_CREATE_GALLERY,
          gallery.containerType,
        );

        dispatch(closeCreateGalleryModal());
        dispatch(galleriesCustomizationActions.openModal());
      } catch (e) {
        const galleryTypeLabel = getTypeOfGalleryLabel(getState());

        crl8.logger.cloud.error(`Failed to create ${galleryTypeLabel}`);

        // Delete only if gallery was created
        if (gallery) {
          await dispatch(
            deleteGalleryActions.deleteGallery(gallery.containerId),
          );
        }

        dispatch(
          toastNotificationsActions.addToastNotification({
            type: ToastType.ERROR,
            title: 'Oops! Something went wrong',
            message: `We could not create your ${galleryTypeLabel}`,
          }),
        );

        dispatch(
          receiveCreateGalleryFailed('An error occurred. Please try again'),
        );
      }
    },

  displayDisconnectedFacebookAccountToast:
    () => (dispatch: ReduxDispatch, getState: ReduxGetState) => {
      crl8.logger.cloud.error(
        `Attempted to create an auto approval config without a connected Facebook or Instagram business account`,
      );

      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Accounts Not Connected',
          message: `Make sure your Instagram and Facebook accounts are connected.`,
          type: ToastType.ERROR,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
            extraData: {
              linkText: 'Connect Your Account',
              callback: () => navigateToState('account-settings.social', {}),
            },
          },
        }),
      );
    },
};
